import { Flex, Text, TextVariants } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'

import type { IPatient } from '@app/domain/patient/patient.interface'
import VisitDateTime from '@app/domain/visit/components/visit-date.component'
import VisitStatus from '@app/domain/visit/components/visit-status.component'
import { getVisitIntakeStartAction } from '@app/domain/visit/visit.helpers'
import type { IVisit } from '@app/domain/visit/visit.interface'
import { SmallButton } from '@app/utils/styles/small-button'

export enum VisitListVariant {
  HidePatient = 'no_patient',
}

const DetailsWrapper = styled.div`
  padding: 0.1rem 0.5rem;
  line-height: normal;
`

const VisitActionGridElement = styled.div`
  display: grid;
  place-items: center;

  grid-column-start: action;
  grid-column-end: action;

  grid-row-start: 1;
  grid-row-end: span 2;
`

const PatientDetailsGridElement = styled.div`
  grid-column-start: details;
  grid-column-end: details;

  grid-row-start: patient;
  grid-row-end: patient;
  display: grid;
  align-items: end;
`

const VisitDetailsGridElement = styled.div`
  grid-column-start: details;
  grid-column-end: details;

  grid-row-start: visit;
  grid-row-end: visit;
`

interface PatientDetailsProps {
  patient: IPatient
}

const VisitRow = styled.div`
  padding-top: 0.25rem;
  padding-top: 0.25rem;
  display: grid;

  grid-template-columns: [action] 7rem [details] auto;
  grid-template-rows: [patient] 3.5rem [visit] 3.5rem;

  ${PatientDetailsGridElement} {
    ${DetailsWrapper} {
      border-radius: 3px;
      background-color: #d6ebff;
      font-weight: bold;
    }
  }
`

const VisitRowWithoutPatient = styled.div`
  padding-top: 0.25rem;
  padding-top: 0.25rem;
  display: grid;

  grid-template-columns: [action] 7rem [details] auto;
  grid-template-rows: [visit] 7rem;

  ${VisitDetailsGridElement} {
    display: grid;
    align-items: center;
  }

  ${DetailsWrapper} {
    line-height: unset;
  }
`

const PatientDetails = ({ patient }: PatientDetailsProps) => {
  return (
    <Flex>
      <DetailsWrapper>
        <NavLink to={`/clinic/patient/${patient.id}`}>
          <Text component={TextVariants.small}>
            {patient.medicalRecordNumber}
            <br />
            {patient.familyName}, {patient.givenNames}
          </Text>
        </NavLink>
      </DetailsWrapper>
    </Flex>
  )
}

interface VisitDetailsProps {
  visit: IVisit
}

const VisitDetails = ({ visit }: VisitDetailsProps) => {
  return (
    <Flex>
      <DetailsWrapper>
        <NavLink to={`/clinic/visit/${visit.id}`}>
          <Text component={TextVariants.small}>
            <VisitDateTime dateTime={visit.startTime} /> at {visit.location}
            <br />
            <VisitStatus status={visit.status} />,{' '}
            {visit.definition ? visit.definition.name : visit.visitType}
          </Text>
        </NavLink>
      </DetailsWrapper>
    </Flex>
  )
}

interface VisitRowProps {
  variant?: VisitListVariant
  visit: IVisit
  onStartIntake: (visit: IVisit) => void
}

export const VisitRowComponent = ({
  onStartIntake,
  variant,
  visit,
}: VisitRowProps) => {
  const startAction = getVisitIntakeStartAction(visit.status)

  const onSelect = useCallback(
    (event) => {
      const item = event.target.getAttribute('value')
      if (item === 'start_intake') {
        onStartIntake(visit)
      }
    },
    [onStartIntake, visit],
  )

  const actionButton = startAction?.show ? (
    <SmallButton
      style={{ width: '5rem', height: '5rem' }}
      variant={startAction.buttonType}
      key="start_intake"
      value="start_intake"
      onClick={onSelect}
    >
      {startAction.label}
    </SmallButton>
  ) : null

  if (variant === VisitListVariant.HidePatient) {
    return (
      <VisitRowWithoutPatient>
        <VisitActionGridElement>{actionButton}</VisitActionGridElement>
        <VisitDetailsGridElement>
          <VisitDetails visit={visit} />
        </VisitDetailsGridElement>
      </VisitRowWithoutPatient>
    )
  } else {
    return (
      <VisitRow>
        <VisitActionGridElement>{actionButton}</VisitActionGridElement>
        <PatientDetailsGridElement>
          <PatientDetails patient={visit.patient} />
        </PatientDetailsGridElement>
        <VisitDetailsGridElement>
          <VisitDetails visit={visit} />
        </VisitDetailsGridElement>
      </VisitRow>
    )
  }
}
